export function buildSelectedDraftItemsTree (nodes, selectedRowIds, missingIds = {}) {
	const missing = {}
	Object.keys(missingIds || {}).forEach((key) => {
		if (missingIds[key]) {
			missing[key] = true
		}
	})

	const itemById = {}
	const nodeParentId = {}
	const itemParentNodeId = {}
	const nodeMatchingItemId = {}
	const itemOrder = []

	const walkNode = (node, parentId = '__root__') => {
		const nodeId = String(node.id)
		nodeParentId[nodeId] = parentId

		const nodeCategoryId = node.category?.id
		const items = node.items || []
		const matchingItem = nodeCategoryId
			? items.find((item) => String(item.category?.id) === String(nodeCategoryId))
			: null
		if (matchingItem) {
			const matchingItemId = String(matchingItem.id)
			nodeMatchingItemId[nodeId] = matchingItemId
			itemById[matchingItemId] = matchingItem
			itemParentNodeId[matchingItemId] = nodeId
			itemOrder.push(matchingItemId)
		}

		items.forEach((item) => {
			const itemId = String(item.id)
			if (itemId === nodeMatchingItemId[nodeId]) return
			itemById[itemId] = item
			itemParentNodeId[itemId] = nodeId
			itemOrder.push(itemId)
		})

		;(node.children || []).forEach((child) => walkNode(child, nodeId))
	}

	;(nodes || []).forEach((node) => walkNode(node))

	const itemOrderIndex = {}
	itemOrder.forEach((itemId, index) => {
		itemOrderIndex[itemId] = index
	})

	const isMissing = (id) => !!missing[String(id)]

	const isExcluded = (rowId) => {
		if (isMissing(rowId)) return true
		let current = itemParentNodeId[rowId] || nodeParentId[rowId]
		while (current && current !== '__root__') {
			if (isMissing(current)) return true
			current = nodeParentId[current]
		}
		return false
	}

	const getParentItemId = (itemId) => {
		let nodeId = itemParentNodeId[itemId]
		while (nodeId && nodeId !== '__root__') {
			const matchingItemId = nodeMatchingItemId[nodeId]
			if (matchingItemId && String(itemId) !== String(matchingItemId)) {
				return matchingItemId
			}
			nodeId = nodeParentId[nodeId]
		}
		return null
	}

	const parentByItemId = {}
	Object.keys(itemParentNodeId).forEach((itemId) => {
		parentByItemId[itemId] = getParentItemId(itemId)
	})

	const childrenByParentId = {}
	Object.keys(parentByItemId).forEach((itemId) => {
		const parentId = parentByItemId[itemId]
		if (parentId) {
			if (!childrenByParentId[parentId]) {
				childrenByParentId[parentId] = []
			}
			childrenByParentId[parentId].push(itemId)
		}
	})

	const selectedItemIds = new Set()
	const rootItemIds = []

	;(selectedRowIds || []).forEach((rowId) => {
		const key = String(rowId)
		const itemId = itemById[key] ? key : nodeMatchingItemId[key]
		if (!itemId) return
		if (selectedItemIds.has(itemId)) return
		if (isExcluded(itemId)) return
		selectedItemIds.add(itemId)
		rootItemIds.push(itemId)
	})

	const nearestSelectedAncestor = (itemId) => {
		let current = itemId
		while (current) {
			if (selectedItemIds.has(current)) {
				return current
			}
			current = parentByItemId[current]
		}
		return null
	}

	const sortByOriginalOrder = (a, b) => {
		return (itemOrderIndex[a] ?? Infinity) - (itemOrderIndex[b] ?? Infinity)
	}

	const buildPayloadChildren = (rootId, itemId) => {
		if (isExcluded(itemId)) return []
		const children = (childrenByParentId[itemId] || [])
			.filter((childId) => !isExcluded(childId) && nearestSelectedAncestor(childId) === rootId)
			.sort(sortByOriginalOrder)
		return children.map((childId) => ({
			id: childId,
			children: buildPayloadChildren(rootId, childId)
		}))
	}

	return rootItemIds
		.sort(sortByOriginalOrder)
		.map((itemId) => ({
			id: itemId,
			children: buildPayloadChildren(itemId, itemId)
		}))
}

export function saleProbabilityColumns () {
	return [
		{ key: 'saleProbability30d', tagKey: 'sale-probability-30d' },
		{ key: 'saleProbability90d', tagKey: 'sale-probability-90d' }
	].map(({ key, tagKey }) => ({
		key,
		sortKey: key,
		widthPreset: 'number',
		name: key,
		tagKey,
		span: 1,
		sortable: true,
		show: false
	}))
}

export function compareSortValues (valA, valB, sortOrder, { nullsLast = false } = {}) {
	const isEmpty = (value) => value === null || value === undefined
	if (nullsLast && (isEmpty(valA) || isEmpty(valB))) {
		if (isEmpty(valA) && isEmpty(valB)) return 0
		return isEmpty(valA) ? 1 : -1
	}
	let a = isEmpty(valA) ? '' : valA
	let b = isEmpty(valB) ? '' : valB
	if (typeof a === 'string') a = a.toLowerCase()
	if (typeof b === 'string') b = b.toLowerCase()
	if (a < b) return sortOrder === 'asc' ? -1 : 1
	if (a > b) return sortOrder === 'asc' ? 1 : -1
	return 0
}

export function getCreateFromDraftErrorMessage (t, errorCode) {
	const errorKey = `grouped-sales-view.inner.mapped-category-parts.create-from-draft.errors.${errorCode}`
	const fallbackKey = 'grouped-sales-view.inner.mapped-category-parts.create-from-draft.errors.PROCESSING_ERROR'

	const hasValue = (key) => {
		const value = t(key)
		return value && value.trim() !== '' && value !== key
	}

	if (hasValue(errorKey)) return t(errorKey)
	if (hasValue(fallbackKey)) return t(fallbackKey)
	return t('common.messages.error')
}

export function normalizeCreateFromDraftResults (results) {
	if (typeof results === 'string') {
		try {
			results = JSON.parse(results)
		} catch (e) {
			results = {}
		}
	}
	if (!results || typeof results !== 'object') {
		results = {}
	}

	const createdParts = Number(results.createdParts || results.created_parts || results.created || results.createdCount || 0)
	const totalParts = Number(results.totalParts || results.total_parts || results.total || results.totalCount || 0)

	return {
		state: results.state || null,
		createdParts,
		totalParts,
		error: results.error || null
	}
}
